import { defineStore } from 'pinia';
import { DashboardService } from '@/services/dashboard';

interface DashboardState {
  /** Bot success rate (percentage 0-100 from the API); null while not loaded */
  successRate: number | null;
  /** Top answers list (with count/displayName); false while not loaded */
  topAnswers: any[] | false;
  top80quantil: number; // 80% Quantil of the top answers
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/dashboard.ts` (namespace
 * `dashboard`). In-memory only — not persisted (no `vuexPersist` option), matching the legacy
 * module which had no entry in either `vuexLocal`/`vuexSession` reducer in `src/store/index.ts`.
 *
 * Collision notes:
 * - Legacy getters `successRate`, `topAnswers`, `top80quantil` were pure identity passthroughs of
 *   the state props of the same name. Pinia already exposes state props directly on the store
 *   instance (`useDashboardStore().successRate`), so — matching the precedent set in
 *   `src/stores/app.ts` for the same situation (`globalSnackbar`/`connectionLost`/`appVersion`) —
 *   these getters are DROPPED entirely rather than renamed. Consumers read the state prop
 *   directly instead of the old getter.
 * - Mutations `setSuccessRate`, `setTopAnswers`, `setTop80Quantil` had no same-named
 *   action/getter, so they convert 1:1 to same-named actions with no renames.
 */
export const useDashboardStore = defineStore('dashboard', {
  state: (): DashboardState => ({
    successRate: null,
    topAnswers: false,
    top80quantil: 0, // 80% Quantil of the top answers
  }),
  actions: {
    async fetchAll(uniqueBotId: string) {
      const bots: any = await DashboardService.fetch(uniqueBotId);
      this.setSuccessRate(bots.successRate);
      return true;
    },
    async fetchTopAnswers({
      uniqueBotId,
      intents,
    }: {
      uniqueBotId: string;
      intents: any[];
    }) {
      const answers: any = await DashboardService.topAnswers(uniqueBotId);
      if (answers && answers.result && answers.result.answers) {
        let all = 0;
        answers.result.answers = answers.result.answers.map((a: any) => {
          all += a.count; // count all Top Counts

          let intent = intents.find((i) => {
            return i.intent === a.name;
          });

          if (intent) {
            a.displayName = intent.displayName;
          } else {
            a.displayName = a.name;
          }

          return a;
        });

        let reached = 0;
        let quantil = answers.result.answers.filter((a: any) => {
          if (reached / all >= 0.8) return false;

          reached += a.count;
          return true;
        });

        this.setTopAnswers(answers.result.answers);
        this.setTop80Quantil(quantil.length);
      }
    },
    clear() {
      this.setTopAnswers(false);
      this.setTop80Quantil(0);
    },
    /**
     * Converted from legacy mutation `setSuccessRate`.
     */
    setSuccessRate(rate: number | null) {
      this.successRate = rate;
    },
    /**
     * Converted from legacy mutation `setTopAnswers`.
     */
    setTopAnswers(answers: any[] | false) {
      this.topAnswers = answers;
    },
    /**
     * Converted from legacy mutation `setTop80Quantil`.
     */
    setTop80Quantil(count: number) {
      this.top80quantil = count;
    },
  },
});
